import { ENVIRONMENT_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { buildTupleString } from 'src/common/helpers/GQLRequestsHelper/buildTupleString';
import {
	ConditionOperators,
	ConditionString,
	ConditionStringArray
} from 'src/common/types/graphql.generated';

interface EnvironmentFilterConfig {
	values: string[];
	customFieldsFilter?: ConditionStringArray;
	environmentsFilter?: ConditionString;
	scoreFilter?: ConditionString;
}

const extractEnvironmentFilter = (
	filters?: IFilterOptions
): Array<{ value: string; label: string }> | undefined => {
	return filters?.[ENVIRONMENT_LABEL_KEY as keyof IFilterOptions] as
		| Array<{ value: string; label: string }>
		| undefined;
};

const addCaseInsensitiveVariants = (values: string[]): string[] => {
	return values.reduce<string[]>((acc, value) => {
		acc.push(value);
		const lowerValue = value.toLowerCase();
		if (lowerValue !== value) {
			acc.push(lowerValue);
		}
		return acc;
	}, []);
};

const buildEnvironmentCustomFieldsFilter = (values: string[]): ConditionStringArray | undefined => {
	if (values.length === 0) {
		return undefined;
	}

	const environmentTuples = values.map((value) => buildTupleString(ENVIRONMENT_LABEL_KEY, value));
	if (environmentTuples.length === 0) {
		return undefined;
	}

	return {
		value: [environmentTuples],
		operator: ConditionOperators.All
	};
};

const buildEnvironmentsFilter = (values: string[]): ConditionString | undefined => {
	if (values.length === 0) {
		return undefined;
	}

	return {
		value: values
	};
};

const buildEnvironmentFilterForScore = (values: string[]): ConditionString | undefined => {
	if (values.length === 0) {
		return undefined;
	}

	return {
		value: values
	};
};

export const buildEnvironmentFilters = (
	filters?: IFilterOptions
): EnvironmentFilterConfig | undefined => {
	const environmentFilter = extractEnvironmentFilter(filters);

	if (!environmentFilter || environmentFilter.length === 0) {
		return undefined;
	}
	const environmentValues = environmentFilter.map((option) => String(option.value));
	const customFieldsFilter = buildEnvironmentCustomFieldsFilter(environmentValues);
	const environmentValuesWithLowercase = addCaseInsensitiveVariants(environmentValues);
	const environmentsFilter = buildEnvironmentsFilter(environmentValuesWithLowercase);
	const scoreFilter = buildEnvironmentFilterForScore(environmentValuesWithLowercase);

	return {
		values: environmentValues,
		customFieldsFilter,
		environmentsFilter,
		scoreFilter
	};
};
